import React from "react";
import style from "../styles/github.module.css";
import { motion } from "framer-motion";
import Reveal from "../components/reveal";
import {
  TbBrandGithub,
  TbArrowUpRight,
  TbStar,
  TbGitFork,
  TbUsers,
  TbBook,
  TbCode,
  TbAlertTriangle,
  TbCalendarStats,
} from "react-icons/tb";
import { TechIcon } from "../components/techIcons";
import useGithubStats from "../hooks/useGithubStats";
import { profile } from "../data/profile";

const USER = profile.github;

const langColors = {
  JavaScript: "#f0c33c",
  TypeScript: "#3178c6",
  Python: "#3776ab",
  Java: "#e76f00",
  HTML: "#e34f26",
  CSS: "#1572b6",
  Dart: "#0175c2",
  "Jupyter Notebook": "#da5b0b",
  Shell: "#89e051",
  PHP: "#777bb4",
  C: "#555555",
  "C++": "#f34b7d",
  Vue: "#41b883",
  SCSS: "#c6538c",
};
const langColor = (n) => langColors[n] || "#8b9cb3";

const Github = () => {
  const { status, profile: gh, repos, languages, totals } = useGithubStats(USER);

  const tiles = [
    { icon: <TbBook />, label: "Public Repos", value: gh?.public_repos },
    { icon: <TbStar />, label: "Stars Earned", value: totals.stars },
    { icon: <TbGitFork />, label: "Forks", value: totals.forks },
    { icon: <TbUsers />, label: "Followers", value: gh?.followers },
  ];

  const joined = gh?.created_at
    ? new Date(gh.created_at).toLocaleDateString("en-GB", {
        month: "short",
        year: "numeric",
      })
    : null;

  return (
    <section id="github" className={`sectionShell ${style.github}`}>
      <div className="sectionInner">
        <Reveal dir="up" amount={0.4}>
          <h2 className="sectionHeading" data-outline="Github">
            Github
          </h2>
        </Reveal>

        <Reveal dir="up" delay={0.05}>
          <p className={style.intro}>
            Live from the GitHub API —{" "}
            <a href={profile.githubUrl} target="_blank" rel="noreferrer">
              @{USER}
            </a>
          </p>
        </Reveal>

        {status === "error" ? (
          <Reveal dir="up">
            <div className={style.errorCard}>
              <TbAlertTriangle />
              <p className={style.errorTitle}>Couldn't reach the GitHub API</p>
              <p className={style.errorHint}>
                Unauthenticated requests are capped at 60 per hour per IP — it
                should come back shortly.
              </p>
              <a href={profile.githubUrl} target="_blank" rel="noreferrer">
                Open the profile instead <TbArrowUpRight />
              </a>
            </div>
          </Reveal>
        ) : (
          <>
            {/* ---------- headline counters ---------- */}
            <div className={style.tiles}>
              {tiles.map((tile, i) => (
                <Reveal
                  dir="up"
                  delay={i * 0.06}
                  amount={0.2}
                  key={tile.label}
                  className={style.tileWrap}
                >
                  <motion.div className={style.tile} whileHover={{ y: -6 }}>
                    <span className={style.tileIcon}>{tile.icon}</span>
                    <span className={`${style.tileValue} gradText`}>
                      {status === "loading" || tile.value == null ? (
                        <span className={style.skeletonText} />
                      ) : (
                        tile.value
                      )}
                    </span>
                    <span className={style.tileLabel}>{tile.label}</span>
                  </motion.div>
                </Reveal>
              ))}
            </div>

            <div className={style.twoCol}>
              {/* ---------- language breakdown ---------- */}
              <Reveal dir="left" delay={0.06} amount={0.12} className={style.colWrap}>
                <motion.div className={style.panel} whileHover={{ y: -5 }}>
                  <div className={style.panelHead}>
                    <span className={style.panelIcon}>
                      <TbCode />
                    </span>
                    <h3>Most Used Languages</h3>
                  </div>

                  {status === "loading" ? (
                    <div className={style.skeletonList}>
                      {[0, 1, 2, 3].map((i) => (
                        <span key={i} className={style.skeletonRow} />
                      ))}
                    </div>
                  ) : languages.length === 0 ? (
                    <p className={style.empty}>No public language data yet.</p>
                  ) : (
                    <ul className={style.langList}>
                      {languages.map((lang, i) => (
                        <li key={lang.name}>
                          <div className={style.langTop}>
                            <span className={style.langName}>
                              <span
                                className={style.langDot}
                                style={{ background: langColor(lang.name) }}
                              />
                              {lang.name}
                            </span>
                            <span className={style.langPct}>{lang.percent}%</span>
                          </div>
                          <span className={style.langTrack}>
                            <motion.span
                              className={style.langFill}
                              style={{ background: langColor(lang.name) }}
                              initial={{ width: 0 }}
                              whileInView={{ width: `${lang.percent}%` }}
                              viewport={{ once: true, amount: 0.5 }}
                              transition={{ duration: 0.8, delay: 0.1 + i * 0.08 }}
                            />
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </motion.div>
              </Reveal>

              {/* ---------- pinned-ish repos ---------- */}
              <Reveal dir="right" delay={0.1} amount={0.12} className={style.colWrap}>
                <motion.div className={style.panel} whileHover={{ y: -5 }}>
                  <div className={style.panelHead}>
                    <span className={style.panelIcon}>
                      <TbBrandGithub />
                    </span>
                    <h3>Recent Work</h3>
                  </div>

                  {status === "loading" ? (
                    <div className={style.skeletonList}>
                      {[0, 1, 2].map((i) => (
                        <span key={i} className={style.skeletonRow} />
                      ))}
                    </div>
                  ) : repos.length === 0 ? (
                    <p className={style.empty}>No public repositories yet.</p>
                  ) : (
                    <ul className={style.repoList}>
                      {repos.map((repo) => (
                        <li key={repo.id}>
                          <a href={repo.html_url} target="_blank" rel="noreferrer">
                            <span className={style.repoName}>
                              {repo.name} <TbArrowUpRight />
                            </span>
                            {repo.description && (
                              <span className={style.repoDesc}>
                                {repo.description}
                              </span>
                            )}
                            <span className={style.repoMeta}>
                              {repo.language && (
                                <span className={style.repoLang}>
                                  <span
                                    className={style.langDot}
                                    style={{ background: langColor(repo.language) }}
                                  />
                                  {repo.language}
                                </span>
                              )}
                              <span>
                                <TbStar /> {repo.stargazers_count}
                              </span>
                              <span>
                                <TbGitFork /> {repo.forks_count}
                              </span>
                            </span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </motion.div>
              </Reveal>
            </div>

            {joined && (
              <Reveal dir="up" delay={0.08}>
                <p className={style.since}>
                  <TbCalendarStats /> On GitHub since {joined}
                  {gh?.following != null && <> · following {gh.following} developers</>}
                </p>
              </Reveal>
            )}
          </>
        )}

        {/* the tech I ship with, as a marquee */}
        <Reveal dir="up" delay={0.1} className={style.marqueeWrap}>
          <div className={style.marquee}>
            <motion.div
              className={style.marqueeTrack}
              animate={{ x: ["0%", "-50%"] }}
              transition={{ duration: 26, repeat: Infinity, ease: "linear" }}
            >
              {[...Array(2)].map((_, dup) =>
                [
                  "Claude API",
                  "OpenAI API",
                  "MCP",
                  "RAG",
                  "n8n",
                  "React",
                  "Node.js",
                  "PostgreSQL",
                  "Docker",
                  "Python",
                  "Linux",
                  "Webhooks",
                ].map((tech) => (
                  <span className={style.marqueeItem} key={`${dup}-${tech}`}>
                    <TechIcon name={tech} /> {tech}
                  </span>
                ))
              )}
            </motion.div>
          </div>
        </Reveal>

        <Reveal dir="up" delay={0.1}>
          <a
            className={style.cta}
            href={profile.githubUrl}
            target="_blank"
            rel="noreferrer"
          >
            <TbBrandGithub /> Browse all repositories <TbArrowUpRight />
          </a>
        </Reveal>
      </div>
    </section>
  );
};

export default Github;
